const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type OccupationSearchResult = {
  id: string;
  preferredLabel: string;
  altLabels: string[];
  description?: string;
  conceptUri: string;
};

export type OccupationCatalogRole = {
  id: string;
  title: string;
  aliases?: string[];
  socCode?: string;
  categoryId: string;
  categoryLabel: string;
  region: string;
  group: string;
};

export type OccupationCatalogCategory = {
  id: string;
  label: string;
  description: string;
  region: string;
  group: string;
  roleCount: number;
};

export type OccupationCatalogResponse = {
  categories: OccupationCatalogCategory[];
  roles: OccupationCatalogRole[];
  totalRoles: number;
  region?: string;
  source?: string;
};

export async function searchOccupations(query: string): Promise<OccupationSearchResult[]> {
  if (!API_BASE_URL) return [];
  const trimmed = query.trim();
  if (!trimmed || trimmed.length < 2) return [];

  const url = new URL(`${API_BASE_URL}/v1/occupations/search`);
  url.searchParams.set('q', trimmed);

  const response = await fetch(url.toString(), { headers: { 'Content-Type': 'application/json' } });
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Occupations search failed (${response.status}): ${text}`);
  }

  const payload = (await response.json()) as { results?: OccupationSearchResult[] };
  return payload.results ?? [];
}

type FallbackCatalogCategory = {
  id: string;
  label: string;
  description: string;
  region: 'uk';
  group: string;
  roles: Array<Omit<OccupationCatalogRole, 'categoryId' | 'categoryLabel' | 'region' | 'group'>>;
};

const FALLBACK_CATALOG_DATA: FallbackCatalogCategory[] = [
  {
    id: 'technology-engineering',
    label: 'Technology & Engineering',
    description: 'Software, data, and infrastructure roles.',
    region: 'uk',
    group: 'SOC 2020 Major Group 2',
    roles: [
      { id: 'software-engineer', title: 'Software Engineer', aliases: ['Software Developer'], socCode: '2136' },
      { id: 'devops-engineer', title: 'DevOps Engineer', aliases: ['Platform Engineer'], socCode: '2137' },
      { id: 'data-analyst', title: 'Data Analyst', aliases: ['MI Analyst'], socCode: '2425' },
      { id: 'cybersecurity-analyst', title: 'Cybersecurity Analyst', aliases: ['Security Analyst'], socCode: '2139' },
      { id: 'product-manager', title: 'Product Manager', aliases: ['Digital Product Manager'], socCode: '1136' },
    ],
  },
  {
    id: 'healthcare-science',
    label: 'Healthcare & Science',
    description: 'Clinical practitioners and allied health.',
    region: 'uk',
    group: 'SOC 2020 Major Group 2',
    roles: [
      { id: 'registered-nurse', title: 'Registered Nurse', aliases: ['Staff Nurse'], socCode: '2231' },
      { id: 'general-practitioner', title: 'General Practitioner', aliases: ['GP'], socCode: '2211' },
      { id: 'pharmacist', title: 'Pharmacist', aliases: ['Community Pharmacist'], socCode: '2213' },
      { id: 'physiotherapist', title: 'Physiotherapist', aliases: ['Physical Therapist'], socCode: '2221' },
      { id: 'healthcare-assistant', title: 'Healthcare Assistant', aliases: ['HCA'], socCode: '6141' },
    ],
  },
  {
    id: 'business-finance-law',
    label: 'Business, Finance & Law',
    description: 'Management, finance, and legal roles.',
    region: 'uk',
    group: 'SOC 2020 Major Group 1/2',
    roles: [
      { id: 'project-manager', title: 'Project Manager', aliases: ['Programme Manager'], socCode: '2424' },
      { id: 'accountant', title: 'Accountant', aliases: ['Chartered Accountant'], socCode: '2421' },
      { id: 'business-analyst', title: 'Business Analyst', aliases: ['BA'], socCode: '2421' },
      { id: 'hr-manager', title: 'HR Manager', aliases: ['People Partner'], socCode: '1135' },
      { id: 'compliance-officer', title: 'Compliance Officer', aliases: ['Risk & Compliance'], socCode: '2422' },
    ],
  },
  {
    id: 'creative-media-marketing',
    label: 'Creative, Media & Marketing',
    description: 'Design, content, and marketing roles.',
    region: 'uk',
    group: 'SOC 2020 Major Group 3/4',
    roles: [
      { id: 'graphic-designer', title: 'Graphic Designer', aliases: ['Visual Designer'], socCode: '3421' },
      { id: 'ux-ui-designer', title: 'UX/UI Designer', aliases: ['Product Designer'], socCode: '3421' },
      { id: 'marketing-manager', title: 'Marketing Manager', aliases: ['Digital Marketing Manager'], socCode: '1132' },
      { id: 'seo-specialist', title: 'SEO Specialist', aliases: ['Search Specialist'], socCode: '2471' },
      { id: 'social-media-manager', title: 'Social Media Manager', aliases: ['Community Manager'], socCode: '2471' },
    ],
  },
  {
    id: 'skilled-trades-service',
    label: 'Skilled Trades & Service',
    description: 'Construction, maintenance, and customer service.',
    region: 'uk',
    group: 'SOC 2020 Major Group 5/6/7',
    roles: [
      { id: 'electrician', title: 'Electrician', aliases: ['Approved Electrician'], socCode: '5241' },
      { id: 'plumber', title: 'Plumber', aliases: ['Gas Engineer'], socCode: '5314' },
      { id: 'carpenter', title: 'Carpenter / Joiner', aliases: ['Joiner'], socCode: '5315' },
      { id: 'chef', title: 'Chef', aliases: ['Head Chef'], socCode: '5434' },
      { id: 'customer-service-advisor', title: 'Customer Service Advisor', aliases: ['Contact Centre Agent'], socCode: '7219' },
    ],
  },
  {
    id: 'education-social-care',
    label: 'Education & Social Care',
    description: 'Teaching and community support roles.',
    region: 'uk',
    group: 'SOC 2020 Major Group 2/6',
    roles: [
      { id: 'primary-teacher', title: 'Primary School Teacher', aliases: ['KS1 Teacher', 'KS2 Teacher'], socCode: '2315' },
      { id: 'secondary-teacher', title: 'Secondary School Teacher', aliases: ['Subject Teacher'], socCode: '2314' },
      { id: 'teaching-assistant', title: 'Teaching Assistant', aliases: ['TA'], socCode: '6125' },
      { id: 'social-worker', title: 'Social Worker', aliases: ['Child Protection Social Worker'], socCode: '2446' },
      { id: 'counsellor', title: 'Counsellor', aliases: ['Therapist'], socCode: '2442' },
    ],
  },
  {
    id: 'public-service-security',
    label: 'Public Service & Security',
    description: 'Frontline public safety roles.',
    region: 'uk',
    group: 'SOC 2020 Major Group 3',
    roles: [
      { id: 'police-officer', title: 'Police Officer', aliases: ['Police Constable'], socCode: '3312' },
      { id: 'firefighter', title: 'Firefighter', aliases: ['Fire Officer'], socCode: '3313' },
      { id: 'prison-officer', title: 'Prison Officer', aliases: ['Custodial Officer'], socCode: '3314' },
      { id: 'probation-officer', title: 'Probation Officer', aliases: ['Offender Manager'], socCode: '3315' },
      { id: 'security-officer', title: 'Security Officer', aliases: ['Security Guard'], socCode: '9241' },
    ],
  },
  {
    id: 'transport-logistics',
    label: 'Transport & Logistics',
    description: 'Driving, warehousing, and supply chain.',
    region: 'uk',
    group: 'SOC 2020 Major Group 8',
    roles: [
      { id: 'hgv-driver', title: 'HGV Driver', aliases: ['LGV Driver'], socCode: '8211' },
      { id: 'delivery-driver', title: 'Delivery Driver', aliases: ['Courier'], socCode: '8212' },
      { id: 'warehouse-operative', title: 'Warehouse Operative', aliases: ['Warehouse Assistant'], socCode: '8171' },
      { id: 'logistics-coordinator', title: 'Logistics Coordinator', aliases: ['Logistics Planner'], socCode: '3537' },
      { id: 'fleet-manager', title: 'Fleet Manager', aliases: ['Transport Manager'], socCode: '1161' },
    ],
  },
];

const normalise = (value: string) => value.trim().toLowerCase();

const buildFallbackCatalog = (query?: string, categoryId?: string): OccupationCatalogResponse => {
  const text = query ? normalise(query) : '';
  const categories = FALLBACK_CATALOG_DATA.filter((category) => !categoryId || category.id === categoryId);

  const roles = categories.flatMap<OccupationCatalogRole>((category) =>
    category.roles.map((role) => ({
      ...role,
      categoryId: category.id,
      categoryLabel: category.label,
      region: category.region,
      group: category.group,
    })),
  );

  const filteredRoles = roles.filter((role) => {
    if (!text) return true;
    const haystack = [role.title, role.categoryLabel, ...(role.aliases ?? [])].map(normalise).join(' ');
    return haystack.includes(text);
  });

  const summaries: OccupationCatalogCategory[] = categories.map((category) => {
    const roleCount = filteredRoles.filter((role) => role.categoryId === category.id).length;
    return {
      id: category.id,
      label: category.label,
      description: category.description,
      region: category.region,
      group: category.group,
      roleCount,
    };
  });

  return {
    categories: summaries,
    roles: filteredRoles,
    totalRoles: filteredRoles.length,
    region: 'uk',
    source: 'fallback',
  };
};

export async function fetchOccupationCatalog(
  query?: string,
  categoryId?: string,
): Promise<OccupationCatalogResponse> {
  const trimmed = query?.trim() ?? '';
  if (!API_BASE_URL) {
    return buildFallbackCatalog(trimmed, categoryId);
  }

  try {
    const url = new URL(`${API_BASE_URL}/v1/occupations/catalog`);
    if (trimmed) url.searchParams.set('q', trimmed);
    if (categoryId) url.searchParams.set('category', categoryId);
    url.searchParams.set('limit', '120');

    const response = await fetch(url.toString(), { headers: { 'Content-Type': 'application/json' } });
    if (!response.ok) {
      throw new Error(`Occupation catalog failed (${response.status})`);
    }

    const payload = (await response.json()) as OccupationCatalogResponse;
    return {
      categories: payload.categories ?? [],
      roles: payload.roles ?? [],
      totalRoles: payload.totalRoles ?? (payload.roles?.length ?? 0),
      region: payload.region ?? 'uk',
      source: payload.source ?? 'api',
    };
  } catch (error) {
    console.warn('[occupations] falling back to local catalog', error);
    return buildFallbackCatalog(trimmed, categoryId);
  }
}
